import { Option } from "@inrev/core";
import {
	type MutationKey,
	type QueryClient,
	type QueryKey,
	useMutation,
	useMutationState,
	useQueryClient,
} from "@tanstack/react-query";
import { useMemo } from "react";
import { type MutationFailureAction, useReportMutationFailure } from "./mutationFailure";
import { applyOptimisticList, restoreById } from "./optimistic";

export type OptimisticListMutationOptions<T, TVariables, TResponse> = {
	queryKey: QueryKey;
	request: (variables: TVariables) => Promise<TResponse>;
	/** Returns the optimistic list. `pendingId` is unique per call, for items that do not exist yet. */
	apply: (items: T[], variables: TVariables, pendingId: string) => T[];
	/** Ids `apply` touches (added, changed, or removed); only these are rolled back on failure. */
	affectedIds: (variables: TVariables, pendingId: string) => string[];
	/** Folds the server response into the cached list. Omit when the optimistic list is final. */
	reconcile?: (items: T[], response: TResponse, variables: TVariables, pendingId: string) => T[];
	/** True when `apply` adds an item under `pendingId`, so the UI can hold it inert until reconciled. */
	creates?: boolean;
	/** Side effect after the response is folded into the cache; runs even if the caller has unmounted. */
	onSuccess?: (response: TResponse, variables: TVariables) => void;
	/** What failed, in the underwriter's terms, shown once the change is rolled back. */
	failureMessage: (variables: TVariables, error: Error) => string;
	/** A follow-up offered with the failure, e.g. reopening the form with what was typed. */
	failureAction?: (variables: TVariables) => MutationFailureAction | undefined;
};

type MutationContext<T> = { previous: T[]; pendingId: string };

const listMutationKey = (queryKey: QueryKey): MutationKey => [...queryKey, "optimistic-mutation"];

const isPendingCreate = (meta: Record<string, unknown> | undefined): boolean =>
	meta?.creates === true;

/**
 * Invalidates a list unless an optimistic mutation on it is in flight: a refetch landing mid-flight
 * would drop that mutation's pending item. The in-flight mutation refetches the list when it settles.
 * `ownMutations` is how many of those in-flight mutations belong to the caller.
 */
export const invalidateListWhenIdle = (
	queryClient: QueryClient,
	queryKey: QueryKey,
	ownMutations = 0,
) => {
	if (queryClient.isMutating({ mutationKey: listMutationKey(queryKey) }) > ownMutations) return;
	return queryClient.invalidateQueries({ queryKey });
};

const readPendingId = (context: unknown): string | undefined =>
	typeof context === "object" &&
	context !== null &&
	"pendingId" in context &&
	typeof context.pendingId === "string"
		? context.pendingId
		: undefined;

/** Ids of items added optimistically to the list at `queryKey` that the server has not confirmed yet. */
export const usePendingIds = (queryKey: QueryKey): ReadonlySet<string> => {
	const pendingIds = useMutationState({
		filters: {
			mutationKey: listMutationKey(queryKey),
			status: "pending",
			predicate: (mutation) => isPendingCreate(mutation.meta),
		},
		select: (mutation) => readPendingId(mutation.state.context),
	});
	return useMemo(() => new Set(Option.filterNone(pendingIds)), [pendingIds]);
};

/**
 * Optimistic mutation over one cached list: cancels fetches, applies the change, rolls back only
 * its own change and reports the failure on error, and reconciles with the server response on
 * success. Once no other mutation on the list is in flight, the list is refetched to converge.
 * It never retries: the change is already on screen, and a retried create could land twice.
 */
export const useOptimisticListMutation = <T extends { id: string }, TVariables, TResponse = void>({
	queryKey,
	request,
	apply,
	affectedIds,
	reconcile,
	creates = false,
	onSuccess,
	failureMessage,
	failureAction,
}: OptimisticListMutationOptions<T, TVariables, TResponse>) => {
	const queryClient = useQueryClient();
	const reportFailure = useReportMutationFailure();
	const mutationKey = listMutationKey(queryKey);

	return useMutation<TResponse, Error, TVariables, MutationContext<T>>({
		mutationKey,
		meta: { creates },
		mutationFn: request,
		retry: false,
		onMutate: async (variables) => {
			const pendingId = crypto.randomUUID();
			const previous = await applyOptimisticList<T>(queryClient, queryKey, (items) =>
				apply(items, variables, pendingId),
			);
			return { previous, pendingId };
		},
		onError: (error, variables, context) => {
			if (context) {
				const ids = affectedIds(variables, context.pendingId);
				queryClient.setQueryData<T[]>(queryKey, (items) =>
					restoreById(items ?? [], context.previous, ids),
				);
			}
			reportFailure({
				error,
				message: failureMessage(variables, error),
				action: failureAction?.(variables),
			});
		},
		onSuccess: (response, variables, context) => {
			if (reconcile) {
				queryClient.setQueryData<T[]>(queryKey, (items) =>
					reconcile(items ?? [], response, variables, context.pendingId),
				);
			}
			onSuccess?.(response, variables);
		},
		onSettled: () => {
			// This mutation still counts as in flight while onSettled runs. The refetch is not
			// awaited, so callers of `mutateAsync` hear about the save without waiting on it; a later
			// mutation cancels it before applying its own change.
			void invalidateListWhenIdle(queryClient, queryKey, 1);
		},
	});
};
